import React from 'react';
import { useTranslation } from 'react-i18next';

import HintButton from 'components/HintButton';
import Loader from 'components/Loader';
import WarningButton from 'components/WarningButton';
import { appReactMemo } from 'hocs';

import Popover from '../../Popover';

import OpenButton from './OpenButton';
import {
  CounterTag,
  CustomLabel,
  IAutocompleteSelect,
  Label,
  PopoverContainer,
  StyledLg,
  StyledMd,
  StyledSm,
} from './styled';

const AutocompleteSelect = ({
  disableUnselect = true,
  enableRemoveButton = true,
  enableTranslation = true,
  errorMessage,
  fullVisibleOption = true,
  hintText,
  inputReadOnly = false,
  maxHintWidth,
  maxWarningWidth,
  options = [],
  size = 'lg',
  warningText,
  ...props
}: IAutocompleteSelect) => {
  const { i18n, t } = useTranslation();

  const withTranslation = React.useMemo(() => {
    return options.map((item) => ({
      ...item,
      label:
        typeof item.label === 'string' &&
        item.label &&
        i18n.exists(item.label) &&
        enableTranslation
          ? t(item.label as never)
          : item.label?.toString(),
    }));
  }, [enableTranslation, i18n, options, t]);

  const errorWithTranslation = errorMessage && t(errorMessage as never);

  // because styled-components 6 doesn't remove unused classes.
  // so when we change the page autocomplete select override other select with different sizes.
  // so we need to add additional class to identify size
  const Component = React.useMemo(() => {
    switch (size) {
      case 'md':
        return StyledMd;
      case 'sm':
        return StyledSm;
      default:
        return StyledLg;
    }
  }, [size]);

  const renderCustomLabel = () => {
    if (!props.isAnimatedLabel && (hintText || warningText)) {
      return (
        <CustomLabel>
          <Label>{props.label}</Label>
          {warningText && (
            <WarningButton maxWarningWidth={maxWarningWidth}>{warningText}</WarningButton>
          )}
          {hintText && <HintButton maxHintWidth={maxHintWidth}>{hintText}</HintButton>}
        </CustomLabel>
      );
    }
  };

  return (
    <Component
      {...props}
      customLabel={renderCustomLabel()}
      disableUnselect={disableUnselect}
      enableRemoveButton={
        enableRemoveButton && props.value !== null && props.value !== undefined
      }
      errorMessage={errorWithTranslation}
      estimateItemSize={size === 'sm' ? 33 : 49}
      fullVisibleOption={fullVisibleOption}
      highlightSearch
      inputReadOnly={inputReadOnly && !!props.value}
      loader={<Loader size={25} />}
      loadingText={t('common.loadingItems')}
      options={withTranslation}
      renderDropdownButton={(props) => <OpenButton {...props} />}
      renderHiddenSelectedOptions={(props) => (
        <Popover
          component={
            <PopoverContainer>
              {props.options.map((item) => (
                <CounterTag key={`${item?.label}-${item?.value}`}>
                  {item.label}
                </CounterTag>
              ))}
            </PopoverContainer>
          }
          placement='bottom'
        >
          <CounterTag>{`+${props.options.length}`}</CounterTag>
        </Popover>
      )}
    />
  );
};

export default appReactMemo(AutocompleteSelect);
